<template lang="pug">
.study-bulk-answer-vertical
  .study-bulk-answer-vertical__content(v-if='!isChangingProblem')
    template(v-for='(problem, pIndex) in studyProblems')
      .study-bulk-answer-vertical__content__list(v-for='(question, qIndex) in problem.questions')
        //- 問題番号表示部
        .study-bulk-answer-vertical__content__list__number.text-large
          .study-bulk-answer-vertical__content__list__number__problem-number
            .study-bulk-answer-vertical__content__list__number__problem-number__label
              slot(name='problemNumberBeforeLabel')
            .study-bulk-answer-vertical__content__list__nunber__problem-number__value {{ problem.problemNumber }}
            .study-bulk-answer-vertical__content__list__number__problem-number__label
              slot(name='problemNumberAfterLabel')
          .study-bulk-answer-vertical__content__list__number__question-number {{ question.questionNumberText }}
        //- 解答入力部
        .study-bulk-answer-vertical__content__list__answer(:data-answer-format='question.answerFormat.name')
          //- 指示文
          .study-bulk-answer-vertical__content__list__answer__question-text(
            v-html='question.indicativeText',
            v-if='question.indicativeText'
          )
          //- テキスト、テキストエリア、プルダウン、手書き/キーボード
          .study-bulk-answer-vertical__content__list__answer__wrapper(v-if='question.questionDatas')
            template(v-for='(data, index) in question.questionDatas')
              component.study-bulk-answer-vertical__content__list__answer__wrapper__input(
                :is='inputComponentType(question.answerFormat)',
                v-model='data.inputValue',
                :beforeText='data.beforeText',
                :afterText='data.afterText',
                :items='data.selectItems',
                :inputType='data.inputType',
                :lang='data.lang',
                :savedStrokes='data.savedStrokes',
                :answerFormat='question.answerFormat',
                :mathKeyboardName='mathKeyboardName',
                @set-input-type='setInputType($event, index, qIndex, pIndex)',
                @set-stroke-data='setStrokeData($event, index, qIndex, pIndex)'
              )
                template(#tegakiResultTitle)
                  slot(name='tegakiResultTitle')
                template(#tegakiRecognizeButtonLabel)
                  slot(name='tegakiRecognizeButtonLabel')
          //- 単一選択、複数選択、並び替え
          .study-bulk-answer-vertical__content__list__answer__wrapper-choice(v-if='question.questionChoiceData')
            component.study-bulk-answer-vertical__content__list__answer__wrapper-choice__choice(
              :is='choiceComponentType(question.answerFormat)',
              v-model='question.questionChoiceData.inputValue',
              :items='question.questionChoiceData.choiceItems',
              :labelNotDisplayable='question.questionChoiceData.labelNotDisplayable',
              :canDeselected='question.questionChoiceData.canDeselectedOrder',
              :isVertical='true'
            )
  //- 次へボタン
  button-next.study-bulk-answer-vertical__button(v-if='showNextButton', @click='nextEvent')
    slot(name='nextButtonLabel')
  button-primary.study-bulk-answer-vertical__button(v-else, @click='completeEvent')
    slot(name='completeButtonLabel')
</template>

<script lang="ts">
import { Component, Prop, Vue } from 'vue-property-decorator'
import InputTextFieldVertical from '@/components/organisms/student/InputTextFieldVertical.vue'
import InputTextareaVertical from '@/components/organisms/student/InputTextareaVertical.vue'
import InputSingleChoice from '@/components/organisms/student/InputSingleChoice.vue'
import InputMultiChoice from '@/components/organisms/student/InputMultiChoice.vue'
import InputDialogVertical from '@/components/organisms/student/InputDialogVertical.vue'
import InputPulldown from '@/components/organisms/student/InputPulldown.vue'
import InputChangeOrder from '@/components/organisms/student/InputChangeOrder.vue'
import ButtonNext from '@/components/molecules/student/ButtonNext.vue'
import ButtonPrimary from '@/components/molecules/student/ButtonPrimary.vue'
import { StudyProblem, InputDialogType } from '@/models/studyModel'
import { StrokePoint } from '@/models/StrokePoint'
import { AnswerFormats, AnswerFormat } from '@/const/AnswerFormats'

@Component({
  components: {
    InputTextFieldVertical,
    InputTextareaVertical,
    InputSingleChoice,
    InputMultiChoice,
    InputDialogVertical,
    InputPulldown,
    InputChangeOrder,
    ButtonNext,
    ButtonPrimary,
  },
})
export default class StudyBulkAnswerVertical extends Vue {
  // 問題切替中フラグ
  @Prop()
  isChangingProblem!: boolean

  // 問題情報群
  @Prop()
  studyProblems!: StudyProblem[]

  // 数学キーボード名
  @Prop()
  mathKeyboardName?: string

  // 次へボタン表示フラグ
  @Prop()
  showNextButton!: boolean

  // 次へボタン押下時イベント
  @Prop()
  nextEvent?: Function

  // 完了ボタン押下時イベント
  @Prop()
  completeEvent!: Function

  // 入力系コンポーネント名取得
  private inputComponentType(answerFormat: AnswerFormat): string {
    // 手書き/ソフトウェアキーボード用
    if (!!answerFormat.isTegaki || !!answerFormat.isKeyboard) {
      return 'InputDialogVertical'
    }

    // それ以外は入力フォーマットに応じたコンポーネントを返す
    switch (answerFormat) {
      case AnswerFormats.TEXT:
        return 'InputTextFieldVertical'
      case AnswerFormats.TEXTAREA:
        return 'InputTextareaVertical'
      case AnswerFormats.MULTI_PULLDOWN:
        return 'InputPulldown'
    }
    return ''
  }

  // 選択系コンポーネント名取得
  private choiceComponentType(answerFormat: AnswerFormat): string {
    switch (answerFormat) {
      case AnswerFormats.SINGLE_CHOICE:
        return 'InputSingleChoice'
      case AnswerFormats.MULTI_CHOICE:
        return 'InputMultiChoice'
      case AnswerFormats.SORT_CHOICE:
        return 'InputChangeOrder'
    }
    return ''
  }

  // 入力方法が手書き/キーボードかをセット
  private setInputType(inputType: InputDialogType, index: number, questionIndex: number, problemIndex: number) {
    const questionDatas = this.studyProblems[problemIndex].questions[questionIndex].questionDatas
    if (questionDatas) {
      questionDatas[index].inputType = inputType
    }
  }

  // 手書きのストローク情報をセット
  private setStrokeData(strokes: StrokePoint[][], index: number, questionIndex: number, problemIndex: number) {
    const questionDatas = this.studyProblems[problemIndex].questions[questionIndex].questionDatas
    if (questionDatas) {
      questionDatas[index].savedStrokes = strokes
    }
  }
}
</script>

<style lang="stylus" scoped>
.study-bulk-answer-vertical
  position relative
  border 2px solid var(--color-study-content__border)
  background-color var(--color-study-content__background)
  border-radius 4px
  padding 20px 10px 20px 50px

  &__content
    writing-mode horizontal-tb
    display flex
    flex-direction row-reverse
    align-items flex-start
    justify-content flex-start
    width 100%
    height 100%
    overflow-y auto

    &__list
      writing-mode vertical-rl
      display flex
      flex-direction column
      padding 0 10px
      height 100%
      &:not(:last-child)
        border-left 1px solid var(--color-study-content__border)

      &__number
        display flex

        &__problem-number
          display flex

      &__answer
        display flex
        flex-direction column
        padding 0 10px

        &__question-text
          margin-left 10px

        &__wrapper
          &__input
            margin 0 0 0 10px
            align-items center
            flex-direction row
            flex-wrap wrap

        &[data-answer-format='multi_pulldown'] &__wrapper,
        &[data-answer-format='science_keyboard'] &__wrapper
          writing-mode horizontal-tb
          &__input
            margin 0 0 10px 0

        &[data-answer-format='textarea'] &__wrapper
          height 100%

  &__button
    position absolute
    display flex
    align-items center
    width var(--input-height-default)
    height 180px
    bottom 40px
    left 10px
</style>
